import jsPDF from "jspdf";
import "jspdf-autotable";
import moment from "moment";

export const exportCareSummaryPdf = async (exportData, isPrint = false) => {
  try {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = 210;

    const margin = 15;

    let y = 20;

    /* =========================
       PATIENT SUMMARY
    ========================== */

    const patientSummary = exportData?.patientSummary || {};

    /* HEADER BAR */

    doc.setFillColor(45, 45, 45);

    doc.rect(margin, y, 180, 8, "F");

    doc.setFont("helvetica", "bold");

    doc.setFontSize(13);

    doc.setTextColor(255, 255, 255);

    doc.text("Patient Care Summary", pageWidth / 2, y + 5.5, {
      align: "center",
    });

    doc.setTextColor(0, 0, 0);

    y += 14;

    /* PATIENT DETAILS TABLE */

    doc.autoTable({
      startY: y,

      body: [
        [
          {
            content: "Patient Name",
            styles: {
              fontStyle: "bold",
            },
          },

          patientSummary?.patient_name || "-",

          {
            content: "PID",
            styles: {
              fontStyle: "bold",
            },
          },

          patientSummary?.pid || "-",

          {
            content: "Date of Birth",
            styles: {
              fontStyle: "bold",
            },
          },

          patientSummary?.dob || "-",
        ],

        [
          {
            content: "Mobile No",
            styles: {
              fontStyle: "bold",
            },
          },

          patientSummary?.mobile_no || "-",

          {
            content: "Email",
            styles: {
              fontStyle: "bold",
            },
          },

          patientSummary?.email || "-",

          {
            content: "Primary Provider",
            styles: {
              fontStyle: "bold",
            },
          },

          patientSummary?.primary_provider || "-",
        ],

        [
          {
            content: "Address",
            styles: {
              fontStyle: "bold",
            },
          },

          {
            content: patientSummary?.address || "-",
            colSpan: 5,
          },
        ],
      ],

      margin: {
        left: margin,
        right: margin,
      },

      styles: {
        fontSize: 8.5,
        cellPadding: 2,
        textColor: [40, 40, 40],
        lineColor: [210, 210, 210],
        lineWidth: 0.15,
        valign: "middle",
        overflow: "hidden",
      },

      columnStyles: {
        // Patient Name label
        0: {
          cellWidth: 30,
          fillColor: [245, 245, 245],
          fontStyle: "bold",
        },

        // Patient Name value
        1: {
          cellWidth: 36,
        },

        // PID label
        2: {
          cellWidth: 20,
          fillColor: [245, 245, 245],
          fontStyle: "bold",
        },

        // PID value
        3: {
          cellWidth: 32,
        },

        // DOB / Provider label
        4: {
          cellWidth: 30,
          fillColor: [245, 245, 245],
          fontStyle: "bold",
        },

        // DOB / Provider value
        5: {
          cellWidth: 32,
        },
      },
      theme: "grid",
    });

    y = doc.lastAutoTable.finalY + 10;

    /* =========================
       SECTION TITLE
    ========================== */

    // const addSectionTitle = (title) => {
    //   const pageHeight = doc.internal.pageSize.height;

    //   // title + minimum table space
    //   if (y > pageHeight - 35) {
    //     doc.addPage();
    //     y = 20;
    //   }

    //   doc.setFont("helvetica", "bold");

    //   doc.setFontSize(11);

    //   doc.setTextColor(33, 37, 41);

    //   doc.text(title, margin, y);

    //   y += 4;
    // };

    const addSectionTitle = (title) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(33, 37, 41);
      doc.text(title, margin, y);
      y += 4;
    };

    /* =========================
       COMMON TABLE
    ========================== */

    const addTable = (headers, body) => {
      doc.autoTable({
        startY: y,

        head: [headers],

        body,

        margin: {
          left: margin,
          right: margin,
        },

        styles: {
          fontSize: 8.5,
          cellPadding: 3,
          valign: "middle",
          overflow: "linebreak",
          textColor: [60, 60, 60],
          lineColor: [220, 220, 220],
          lineWidth: 0.2,
        },

        headStyles: {
          fillColor: [240, 240, 240],
          textColor: [0, 0, 0],
          fontStyle: "bold",
        },

        bodyStyles: {
          textColor: [60, 60, 60],
        },

        theme: "grid",
      });

      y = doc.lastAutoTable.finalY + 10;
    };

    /* =========================
       DYNAMIC SECTIONS
    ========================== */

    const sections = [
      {
        title: "Active Problems",
        configKey: "Problems",

        data: exportData?.problems,

        headers: ["Problems", "Diagnosed", "Status"],

        rows: exportData?.problems?.map((item) => [
          item.title || "-",
          item.subText || "-",
          item.status || "-",
        ]),
      },

      {
        title: "Allergies",
        configKey: "Allergies",

        data: exportData?.allergies,

        headers: ["Allergies", "Added", "Status"],

        rows: exportData?.allergies?.map((item) => [
          item.title || "-",
          item.subText || "-",
          item.status || "-",
        ]),
      },

      {
        title: "Current Medications",
        configKey: "Medications",

        data: exportData?.medications,

        headers: ["Medication", "Prescribed", "Status"],

        rows: exportData?.medications?.map((item) => [
          item.title || "-",
          item.subText || "-",
          item.status || "-",
        ]),
      },

      {
        title: "Recent Appointments",
        configKey: "Recent Appointments",

        data: exportData?.appointments,

        headers: ["Providers", "Facility", "Date & Time"],

        rows: exportData?.appointments?.map((item) => [
          item.name || "-",
          item.place || "-",
          item.date || "-",
        ]),
      },

      {
        title: "Health Screening",
        configKey: "Health Screening",

        data: exportData?.screenings,

        headers: ["Screening", "Age", "Due Date", "Status"],

        rows: exportData?.screenings?.map((item) => [
          item.title || "-",
          item.age || "-",
          item.due || "-",
          item.status || "-",
        ]),
      },

      {
        title: "Vital Sign",
        configKey: "Vitals",
        data: exportData?.vitals,

        headers: ["Metric", "Reading"],

        rows: exportData?.vitals?.map((item) => [
          item.label || "-",
          item.value || "-",
        ]),
      },

      {
        title: "Immunization",
        configKey: "Immunizations",
        data: exportData?.immunizations,

        headers: ["Vaccine", "Administered By", "Date & Time", "Status"],

        rows: exportData?.immunizations?.map((item) => [
          item.vaccineName || "-",
          item.providerName || "-",
          item.administeredDate || "-",
          item.status || "-",
        ]),
      },

      {
        title: "Procedures & Surgeries",
        configKey: "Procedure & Surgeries",

        data: exportData?.procedures,

        headers: ["Procedure", "Code", "Date", "Status"],

        rows: exportData?.procedures?.map((item) => [
          item.procedureName || "-",
          item.codeType || "-",
          item.procedureDate || "-",
          item.status || "-",
        ]),
      },

      {
        title: "Recent Lab Results",
        configKey: "Lab Results",

        data: exportData?.labs,

        headers: ["Test", "Result", "Date", "Status"],

        rows: exportData?.labs?.map((item) => [
          item.testName || "-",
          item.interpretation || "-",
          item.resultDate
            ? moment(item.resultDate).format("MMM DD, YYYY")
            : "-",
          item.status || "-",
        ]),
      },

      {
        title: "Social History",
        configKey: "Social History",

        data: exportData?.socialHistory,
        headers: ["Category", "Details", "Notes"],

        rows: exportData?.socialHistory?.flatMap((item) => [
          [
            "Smoking Status",
            `${item.smokingStatus || "-"} ${
              item.smokingHowMuch ? `- ${item.smokingHowMuch}` : ""
            }`,
            item.smokedSinceAge
              ? `Smoked Since Age: ${item.smokedSinceAge}`
              : "-",
          ],

          ["Alcohol Use", item.alcoholIntake || "-", "-"],

          [
            "Tobacco",
            item.chewingTobacco || "-",
            item.tobaccoYearsOfUse
              ? `Tobacco years of use: ${item.tobaccoYearsOfUse}`
              : "-",
          ],
        ]),
      },

      {
        title: "Family History",
        configKey: "Family History",

        data: exportData?.familyHistory,

        headers: ["Condition", "Relation", "Documented"],

        rows: exportData?.familyHistory?.map((item) => [
          item.observation || "-",

          `${
            item.relationship
              ? item.relationship.charAt(0).toUpperCase() +
                item.relationship.slice(1).toLowerCase()
              : "-"
          }${item.age ? ` - age ${item.age}` : ""}`,

          item.created_at
            ? moment(item.created_at).format("MMM DD, YYYY")
            : "-",
        ]),
      },

      {
        title: "Alerts & Clinical Warning",
        configKey: "Alerts & Clinical Warning",

        data: exportData?.alerts,

        headers: ["Alert", "Value", "Date", "Risk"],

        rows: exportData?.alerts?.map((item) => [
          item.testName || "-",
          item.value || "-",
          item.triggeredOn &&
          moment(item.triggeredOn, "DD/MM/YYYY", true).isValid()
            ? moment(item.triggeredOn, "DD/MM/YYYY").format("MMM DD, YYYY")
            : "-",
          item.status || "-",
        ]),
      },

      {
        title: "Treatment Plan",
        configKey: "Treatment Plan",
        data: exportData?.treatmentPlans,

        headers: ["Plan", "Provider", "End Date", "Progress"],

        rows: exportData?.treatmentPlans?.map((item) => [
          item.treatmentPlan || "-",
          item.provider || "-",
          item.endDate || "-",
          `${item.completedGoals || 0}/${item.totalGoals || 0}`,
        ]),
      },

      {
        title: "Care Plan",
        configKey: "Care Plan",

        data: exportData?.carePlans,

        headers: ["Plan", "ICD Code", "Date", "Status"],

        rows: exportData?.carePlans?.map((item) => [
          item.planName || "-",
          item.icd || "-",
          item.updatedDate || "-",
          item.status
            ? item.status.charAt(0).toUpperCase() +
              item.status.slice(1).toLowerCase()
            : "-",
        ]),
      },

      {
        title: "Care Team",
        configKey: "Care Team",

        data: exportData?.careTeams,

        headers: ["Name", "Role", "Care Plan"],

        rows: exportData?.careTeams?.map((item) => [
          item.providerName || "-",
          item.role || "-",
          item.carePlan || "-",
        ]),
      },
    ];

    /* =========================
       RENDER ALL SECTIONS
    ========================== */

    let visibleSectionIndex = 1;

    // sections.forEach((section) => {
    //   if (section?.data && section.data.length > 0) {
    //     addSectionTitle(`${visibleSectionIndex}. ${section.title}`);

    //     addTable(section.headers, section.rows);

    //     visibleSectionIndex++;
    //   }
    // });
    sections.forEach((section) => {
      if (
        exportData?.patientSummary?.care_summary_config &&
        exportData.patientSummary.care_summary_config[section.configKey] ===
          false
      ) {
        return;
      }
      const hasData =
        section?.data && Array.isArray(section.data) && section.data.length > 0;

      const rows = hasData
        ? section.rows
        : [new Array(section.headers.length).fill("-")];
      const pageHeight = doc.internal.pageSize.height;

      // const estimatedHeight = 15 + rows.length * 10;

      //   const pageHeight = doc.internal.pageSize.height;

      //   if (y + estimatedHeight > pageHeight - 20) {
      //     doc.addPage();
      //     y = 20;
      //   }

      //   addSectionTitle(`${visibleSectionIndex}. ${section.title}`);

      //   addTable(section.headers, rows);

      const titleHeight = 10;

      const minTableHeight = 20;

      const estimatedHeight = titleHeight + minTableHeight;

      if (y + estimatedHeight > pageHeight - 20) {
        doc.addPage();
        y = 20;
      }

      addSectionTitle(`${visibleSectionIndex}. ${section.title}`);

      addTable(section.headers, rows);

      visibleSectionIndex++;
    });

    /* =========================
       FOOTER
    ========================== */

    const totalPages = doc.internal.getNumberOfPages();

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);

      doc.setFontSize(8);

      doc.setTextColor(90, 90, 90);

      doc.text(`Page ${i} of ${totalPages}`, pageWidth - 25, 290);
    }

    /* =========================
       OUTPUT
    ========================== */

    if (isPrint) {
      doc.autoPrint();

      window.open(doc.output("bloburl"));
    } else {
      doc.save(`CareSummary_${moment().format("YYYYMMDD_HHmmss")}.pdf`);
    }
  } catch (error) {
    console.log("Care Summary PDF Error", error);
  }
};
